<template>
  <div class="min-h-screen bg-gray-900">
    <div
      class="sticky top-0 bg-blue-600 p-4 text-center flex items-center justify-center space-x-2 text-white z-40"
      v-if="timeRemaining > 0"
    >
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>Running Free Link Analysis Reports for {{ formatTime(timeRemaining) }}</span>
      </div>
    </div>

    <!-- Hero Section -->
    <div class="min-h-screen flex flex-col">
      <div class="flex-1 container mx-auto px-4 flex flex-col items-center justify-center text-center gap-4">
        <!-- Authority Badge -->
        <div class="bg-blue-500/10 border border-blue-500/20 text-blue-400 px-6 my-2 rounded-full text-sm">
          We've been securing link placements for over a decade.
        </div>

        <h1 class="text-5xl md:text-6xl font-bold text-white max-w-4xl pb-4">
          Know <em class="text-blue-400 not-italic">exactly</em> how many links to build to
          <span class="border-b-4 border-blue-500">beat your competition</span>
        </h1>

        <p class="text-xl md:text-2xl text-gray-300 max-w-3xl">
          Stop second guessing your link building strategy.
          Get data-driven recommendations tailored to your website and competitors–in just a few clicks.

        </p>

        <!-- Primary CTA -->
        <button
            @click="showForm = true"
            class="group bg-green-500 hover:bg-green-600 text-white text-xl px-8 py-4 rounded-lg font-semibold transition-all transform hover:scale-105 shadow-lg"
          >
            Get My Free Link Analysis Report
            <span class="block text-sm opacity-90">
              Free Analysis Available for {{ formatTime(timeRemaining) }}
            </span>
          </button>


        <!-- Video Section -->
        <div class="w-full max-w-2xl aspect-video bg-gray-800 rounded-lg mt-8">
          <video
            ref="videoPlayer"
            class="w-full h-full object-cover rounded-lg"
            autoplay
            loop
            muted
            playsinline
          >
            <source src="https://storage.googleapis.com/web20ranker-courses/cost_modeling_workbook.mp4" type="video/mp4">
          </video>
        </div>
      </div>

      <!-- Features Section -->
      <div class="bg-gray-900 py-20">
        <div class="container mx-auto px-4">
          <h2 class="text-4xl font-bold text-center mb-12 text-white">
            What’s Included in Your Link Recommendation Report
          </h2>

          <ul class="max-w-3xl mx-auto space-y-6 mb-12">
            <li
              v-for="feature in features"
              :key="feature"
              class="flex items-start gap-4"
            >
              <svg class="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <span class="text-lg text-gray-300">{{ feature }}</span>
            </li>
          </ul>

          <div class="text-center">
            <button
              @click="showForm = true"
              class="bg-green-500 hover:bg-green-600 text-white text-xl px-8 py-4 rounded-lg font-semibold transition-colors"
            >
              Get My Link Recommendation
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Multi-Step Form Modal -->
    <Teleport to="body">
      <div
        v-if="showForm"
        class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      >
        <div class="bg-white rounded-xl shadow-2xl max-w-md w-full relative">
          <!-- Close Button -->
          <button
            @click="handleCloseModal"
            class="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          >
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <!-- Progress Bar -->
          <div class="bg-gray-200 h-2 rounded-t-xl">
            <div
              class="bg-blue-600 h-2 rounded-t-xl transition-all duration-300"
              :style="{ width: `${(currentStep / totalSteps) * 100}%` }"
            />
          </div>

          <div class="p-6">
            <h3 class="text-2xl font-bold text-gray-900 mb-1">
              {{ stepTitles[currentStep - 1] }}
            </h3>
            <p class="text-gray-600 mb-6">
              Step {{ currentStep }} of {{ totalSteps }} • {{ timeRemaining > 0 ? `Ending in ${formatTime(timeRemaining)}` : 'Limited time offer' }}
            </p>

            <form
              @submit.prevent="currentStep === TOTAL_STEPS ? handleSubmit() : null"
              @keydown.enter.prevent="handleEnterKey"
              class="space-y-4"
            >
              <!-- Step 1: Website URL -->
              <template v-if="currentStep === 1">
                <div class="space-y-4">
                  <div>
                    <label for="websiteUrl" class="block text-sm font-medium text-gray-700 mb-1">
                      Your Website URL
                    </label>
                    <input
                      id="websiteUrl"
                      ref="websiteUrlInput"
                      v-model="formData.websiteUrl"
                      type="text"
                      placeholder="https://example.com"
                      class="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      :class="{ 'border-red-500': formErrors.websiteUrl }"
                      required
                    />
                    <p v-if="formErrors.websiteUrl" class="mt-1 text-sm text-red-600">
                      {{ formErrors.websiteUrl }}
                    </p>
                  </div>
                  <button
                    type="button"
                    @click="nextStep"
                    class="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center space-x-2"
                    :disabled="!formData.websiteUrl"
                  >
                    <span>Next: Enter Target Keyword</span>
                    <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </template>

              <!-- Step 2: Target Keyword -->
              <template v-if="currentStep === 2">
                <div class="space-y-4">
                  <div>
                    <label for="targetKeyword" class="block text-sm font-medium text-gray-700 mb-1">
                      Primary Keyword to Rank For
                    </label>
                    <input
                      id="targetKeyword"
                      ref="targetKeywordInput"
                      v-model="formData.targetKeyword"
                      type="text"
                      placeholder="e.g. best running shoes"
                      class="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      :class="{ 'border-red-500': formErrors.targetKeyword }"
                      required
                    />
                    <p v-if="formErrors.targetKeyword" class="mt-1 text-sm text-red-600">
                      {{ formErrors.targetKeyword }}
                    </p>
                  </div>
                  <Multiselect
                    v-model="formData.location"
                    placeholder="Search and select location"
                    :noOptionsText="`Enter location to search`"
                    :options="fetchLocations"
                    :searchable="true"
                    :filter-results="false"
                    :resolve-on-load="false"
                    :min-chars="2"
                    :delay="300"
                    track-by="value"
                    label="label"
                  />
                  <button
                    type="button"
                    @click="nextStep"
                    class="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center space-x-2"
                    :disabled="!formData.targetKeyword"
                  >
                    <span>Next: Your Contact Details</span>
                    <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </template>

              <!-- Step 3: Contact Information -->
              <template v-if="currentStep === 3">
                <div class="space-y-4">
                  <div>
                    <label for="firstName" class="block text-sm font-medium text-gray-700 mb-1">
                      Your First Name
                    </label>
                    <input
                      id="firstName"
                      ref="firstNameInput"
                      v-model="formData.firstName"
                      type="text"
                      placeholder="John"
                      class="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      :class="{ 'border-red-500': formErrors.firstName }"
                      required
                    />
                  </div>
                  <div>
                    <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                      Business Email
                    </label>
                    <input
                      id="email"
                      v-model="formData.email"
                      type="email"
                      placeholder="john@example.com"
                      class="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      :class="{ 'border-red-500': formErrors.email }"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    class="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-semibold transition-colors"
                    :disabled="loading"
                  >
                    <span v-if="loading">Processing...</span>
                    <span v-else>Get My Free Analysis Now</span>
                  </button>
                </div>
              </template>

              <!-- Trust Indicators -->
              <div class="mt-6 pt-6 border-t">
                <div class="flex items-center justify-center space-x-4">
                  <div class="flex items-center text-sm text-gray-500">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Your data is secure
                  </div>
                  <div class="flex items-center text-sm text-gray-500">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    No credit card required
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>
  </div>

  <div class="py-20 bg-gray-100">
      <div class="container mx-auto px-4">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img :src="processImageUrl" alt="Process visualization" class="rounded-lg shadow-xl">

            <div class="mt-8 text-center">
              <button
                @click="showForm = true"
                class="bg-blue-600 hover:bg-blue-700 text-white text-xl px-8 py-4 rounded-lg font-semibold transition-colors">
                Get My Link Recommendation
              </button>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-6">
            <div v-for="step in processSteps"
                 :key="step.number"
                 class="bg-white p-6 rounded-lg shadow-lg">
              <div class="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-4">
                {{ step.number }}
              </div>
              <h3 class="text-xl font-bold mb-2">{{ step.title }}</h3>
              <p class="text-gray-600">{{ step.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { createToast } from 'mosha-vue-toastify'
import { hideLoadingSpinner } from '@/helpers/hide_loader.js'
import Multiselect from '@vueform/multiselect'
import { searchLocations } from '@/api/clients/search_api_locations'
import { createLinkRecommendation } from '@/api/clients/link_recommendations'
import '@vueform/multiselect/themes/default.css'
import 'mosha-vue-toastify/dist/style.css'
import processImageUrl from '~/images/link-rec-example.png'

// Constants
const VIDEO_URL = 'https://storage.googleapis.com/web20ranker-courses/cost_modeling_workbook.mp4'
const INITIAL_TIMER = 36 * 60 // 15 minutes
const TOTAL_STEPS = 3
const totalSteps = 3
const STORAGE_KEY = 'recentOptIns'
const MAX_STORED_SIGNUPS = 15
const MIN_SIGNUP_INTERVAL = 2 * 60 * 60 * 1000 // 2 hours
const MAX_SIGNUP_INTERVAL = 4 * 60 * 60 * 1000 // 4 hours

const websiteUrlInput = ref(null)
const targetKeywordInput = ref(null)
const firstNameInput = ref(null)

// Component refs
const videoPlayer = ref(null)
const showForm = ref(false)
const currentStep = ref(1)
const loading = ref(false)
let timerInterval = null

// Form state
const formData = ref({
  websiteUrl: '',
  targetKeyword: '',
  firstName: '',
  email: '',
  location: null
})

const formErrors = ref({
  websiteUrl: '',
  targetKeyword: '',
  firstName: '',
  email: ''
})

// Step content
const stepTitles = [
  'Enter Your Website',
  'What Do You Want to Rank For?',
  'Where Should We Send Your Report?'
]

const handleEnterKey = (e) => {
  // If we're on the final step, let the form handle submission normally
  if (currentStep === TOTAL_STEPS) {
    return
  }

  // For non-final steps, pressing Enter should advance to next step
  if (validateForm()) {
    nextStep()
  }
}

const processSteps = [
  {
    number: 1,
    title: 'Analysis',
    description: "Breakdown your site's current link profile and ranking position"
  },
  {
    number: 2,
    title: 'Comparison',
    description: 'Benchmark your link profiles against top-ranking competitors'
  },
  {
    number: 3,
    title: 'Strategy',
    description: 'Prioritize high-impact actions with a tailored link building strategy'
  },
  {
    number: 4,
    title: 'Action Plan',
    description: 'Unlock clear recommendations for implementation'
  }
]

const features = [
  'Detailed analysis of your current link profile vs competitors',
  'Exact number of links needed to overtake competition',
  'Prioritized action plan for link acquisition',
  'Competitor link gap analysis',
]

const realisticSignups = [
  { firstName: 'Tom', email: 'tom@digitalagency.com' },
  { firstName: 'Sarah', email: 'sarah@ecommerce.io' },
  { firstName: 'Mike', email: 'mike@localmarketing.com' },
  { firstName: 'Emma', email: 'emma@techfirm.co' },
  { firstName: 'John', email: 'john@seoagency.net' },
  { firstName: 'Lisa', email: 'lisa@webdesign.com' },
  { firstName: 'David', email: 'david@marketing.io' },
  { firstName: 'Amy', email: 'amy@digitalfirst.com' },
  { firstName: 'James', email: 'james@seoexperts.com' },
  { firstName: 'Karen', email: 'karen@rankpro.net' },
  { firstName: 'Alex', email: 'alex@searchpros.com' },
  { firstName: 'Rachel', email: 'rachel@webagency.io' }
]

const initializeSignups = () => {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored) {
    return JSON.parse(stored)
  }

  // Create initial signups with realistic timestamps
  const initialSignups = []
  let timestamp = Date.now()

  for (let i = 0; i < 3; i++) {
    const signup = realisticSignups[Math.floor(Math.random() * realisticSignups.length)]
    initialSignups.push({
      id: timestamp - (i * 1000), // Ensure unique IDs
      firstName: signup.firstName,
      email: signup.email,
      timestamp: timestamp - (Math.random() * 3600000) // Random time within last hour
    })
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(initialSignups))
  return initialSignups
}

const recentSignups = ref(initializeSignups())


const validateForm = () => {
  formErrors.value = {}
  let isValid = true

  // Only validate fields for current step
  switch (currentStep.value) {
    case 1:
      if (!formData.value.websiteUrl) {
        formErrors.value.websiteUrl = 'Please enter your website URL'
        isValid = false
      } else if (!/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.value.websiteUrl)) {
        formErrors.value.websiteUrl = 'Please enter a valid website URL'
        isValid = false
      }
      break

    case 2:
      if (!formData.value.targetKeyword) {
        formErrors.value.targetKeyword = 'Please enter your target keyword'
        isValid = false
      }
      break

    case 3:
      if (!formData.value.firstName) {
        formErrors.value.firstName = 'Please enter your name'
        isValid = false
      }
      if (!formData.value.email) {
        formErrors.value.email = 'Please enter your email'
        isValid = false
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)) {
        formErrors.value.email = 'Please enter a valid email address'
        isValid = false
      }
      break
  }

  return isValid
}

const nextStep = () => {
  if (validateForm() && currentStep.value < TOTAL_STEPS) {
    currentStep.value++
    nextTick(() => {
      focusCurrentInput()
    })
  }
}

const focusCurrentInput = () => {
  switch (currentStep.value) {
    case 1:
      websiteUrlInput.value?.focus()
      break
    case 2:
      targetKeywordInput.value?.focus()
      break
    case 3:
      firstNameInput.value?.focus()
      break
  }
}

const previousStep = async () => {
  if (currentStep.value > 1) {
    currentStep.value--
    nextTick(() => {
      focusCurrentInput()
    })
  }
}


const formatTimeAgo = (timestamp) => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)

  if (seconds < 60) return 'just now'

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

const maskEmail = (email) => {
  const [username, domain] = email.split('@')
  const maskedUsername = username.slice(0, 0) + '*'.repeat(username.length - 1)
  const [domainName, tld] = domain.split('.')
  const maskedDomain = domainName.slice(0, 0) + '*'.repeat(domainName.length - 1)
  return `${maskedUsername}@${maskedDomain}.${tld}`
}

const handleCloseModal = () => {
  // Show exit intent confirmation if user has started filling the form
  if (formData.value.websiteUrl || formData.value.targetKeyword || formData.value.email) {
    if (confirm('Are you sure you want to leave? Your custom link analysis report is almost ready!')) {
      showForm.value = false
    }
  } else {
    showForm.value = false
  }
}

const fetchLocations = async (query) => {
  if (query.length < 2) return []
  try {
    const response = await searchLocations({ query })
    return response.data?.locations || []
  } catch (error) {
    console.error('Error fetching locations:', error)
    return []
  }
}

const updateSignups = () => {
  // Get a random signup that hasn't been used recently
  const recentEmails = new Set(recentSignups.value.map(s => s.email))
  const availableSignups = realisticSignups.filter(s => !recentEmails.has(s.email))
  const signup = availableSignups[Math.floor(Math.random() * availableSignups.length)]

  const newSignup = {
    id: Date.now(),
    firstName: signup.firstName,
    email: signup.email,
    timestamp: Date.now()
  }

  recentSignups.value = [newSignup, ...recentSignups.value.slice(0, -1)]
  localStorage.setItem(STORAGE_KEY, JSON.stringify(recentSignups.value))
}


// Add these constants at the top of the script
const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 3600
const SECONDS_PER_DAY = 86400
const MIN_INITIAL_SECONDS = 40 * SECONDS_PER_HOUR // 40 hours
const MAX_INITIAL_SECONDS = 52 * SECONDS_PER_HOUR // 52 hours

// Update timer initialization logic
const initializeTimer = () => {
  const storedEndTime = localStorage.getItem('linkAnalysisEndTime')
  const now = Date.now()

  if (storedEndTime) {
    // Calculate remaining time from stored end time
    const remainingMs = parseInt(storedEndTime) - now
    if (remainingMs > 0) {
      return Math.floor(remainingMs / 1000)
    }
  }

  // Generate new random duration between 40-52 hours
  const randomDuration = Math.floor(
    Math.random() * (MAX_INITIAL_SECONDS - MIN_INITIAL_SECONDS) + MIN_INITIAL_SECONDS
  )

  // Store end time
  localStorage.setItem('linkAnalysisEndTime', (now + (randomDuration * 1000)).toString())

  return randomDuration
}

// Replace the existing time formatting function
const formatTime = (seconds) => {
  const days = Math.floor(seconds / SECONDS_PER_DAY)
  const hours = Math.floor((seconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR)
  const minutes = Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE)

  const parts = []

  if (days > 0) {
    parts.push(`${days} day${days !== 1 ? 's' : ''}`)
  }
  if (hours > 0 || days > 0) {
    parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`)
  }
  parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`)

  return parts.join(' ')
}

// Update timer initialization in setup
const timeRemaining = ref(initializeTimer())

// Update the timer start function
const startTimer = () => {
  timerInterval = setInterval(() => {
    if (timeRemaining.value > 0) {
      timeRemaining.value--
      // Update stored end time periodically to handle tab switches/reloads
      if (timeRemaining.value % 30 === 0) { // Update every 30 seconds
        const now = Date.now()
        localStorage.setItem('linkAnalysisEndTime',
          (now + (timeRemaining.value * 1000)).toString()
        )
      }
    } else {
      clearInterval(timerInterval)
      localStorage.removeItem('linkAnalysisEndTime')
    }
  }, 1000)
}

const handleSubmit = async () => {
  // Validate all fields before final submission
  formErrors.value = {}
  let isValid = true

  if (!formData.value.websiteUrl || !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.value.websiteUrl)) {
    formErrors.value.websiteUrl = 'Please enter a valid website URL'
    isValid = false
  }

  if (!formData.value.targetKeyword) {
    formErrors.value.targetKeyword = 'Please enter your target keyword'
    isValid = false
  }

  if (!formData.value.firstName) {
    formErrors.value.firstName = 'Please enter your name'
    isValid = false
  }

  if (!formData.value.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)) {
    formErrors.value.email = 'Please enter a valid email address'
    isValid = false
  }

  if (!isValid) {
    createToast('Please complete all required fields', {
      type: 'warning',
      position: 'bottom-center',
      timeout: 5000
    })
    return
  }

  loading.value = true
  try {
    let submissionUrl = formData.value.websiteUrl.trim()
    if (!submissionUrl.match(/^https?:\/\//i)) {
      submissionUrl = `https://${submissionUrl}`
    }

    const response = await createLinkRecommendation({
      firstName: formData.value.firstName,
      email: formData.value.email,
      url: submissionUrl,
      keyword: formData.value.targetKeyword,
      location: formData.value.location || null
    })

    if (response.data?.id) {
      window.location.href = `/link-recommendation/${response.data.id}?processing=true`
    } else {
      throw new Error('Invalid response from server')
    }
  } catch (err) {
    const errorMessage = err.response?.data?.error || 'An error occurred. Please try again.'
    createToast(errorMessage, {
      type: 'danger',
      position: 'bottom-center',
      timeout: 5000,
      showIcon: true
    })
  } finally {
    loading.value = false
  }
}

// Lifecycle hooks
onMounted(() => {
  hideLoadingSpinner()

  nextTick(() => {
    focusCurrentInput()
  })

  startTimer()
  // Start video playback
  if (videoPlayer.value) {
    videoPlayer.value.play().catch(err => console.error('Video autoplay failed:', err))
  }
  const scheduleNextSignup = () => {
    const delay = Math.floor(
      Math.random() * (MAX_SIGNUP_INTERVAL - MIN_SIGNUP_INTERVAL) + MIN_SIGNUP_INTERVAL
    )
    setTimeout(() => {
      updateSignups()
      scheduleNextSignup()
    }, delay)
  }

  scheduleNextSignup()
})

onBeforeUnmount(() => {
  if (timerInterval) {
    clearInterval(timerInterval)
  }
})
</script>

<style>
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* Multiselect customization */
:deep(.multiselect) {
  border-color: rgb(209 213 219);
  border-radius: 0.5rem;
}

:deep(.multiselect-option) {
  padding: 0.75rem 1rem;
}

:deep(.multiselect-option.is-selected) {
  background-color: rgb(239 246 255);
  color: rgb(37 99 235);
}

:deep(.multiselect-option.is-pointed) {
  background-color: rgb(243 244 246);
  color: rgb(17 24 39);
}

:deep(.multiselect-search) {
  background-color: white;
}

:deep(.multiselect-dropdown) {
  background-color: white;
  border-color: rgb(209 213 219);
}

:deep(.multiselect-tags) {
  padding: 0.5rem;
  background-color: white;
}

:deep(.multiselect-tag) {
  background-color: rgb(37 99 235);
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  margin-right: 0.5rem;
}

/* Video styling */
.video-wrapper {
  position: relative;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
}

.video-wrapper video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Animation utilities */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: translateY(-20px);
}

/* Form input focus states */
input:focus,
select:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
  --tw-ring-color: rgb(59 130 246);
  --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color);
  box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
  border-color: rgb(59 130 246);
}

/* Error states */
.error-shake {
  animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both;
}

@keyframes shake {
  10%, 90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%, 80% {
    transform: translate3d(2px, 0, 0);
  }

  30%, 50%, 70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%, 60% {
    transform: translate3d(4px, 0, 0);
  }
}

/* Utility classes */
.min-h-screen {
  min-height: 100vh;
}

.backdrop-blur {
  backdrop-filter: blur(8px);
}
</style>
